import Dexie, { type Table } from "dexie";

/**
 * 本机草稿：断网或保存失败时，输入必须先落到设备上。
 *
 * 按 userId + chapterId 隔离：同一台设备换账号后不能读到、也不能误发
 * 上一个账号的草稿（见 docs/backend-contracts.md 第 3 节）。
 */
export interface LocalDraft {
  /** userId:chapterId —— 主键里带用户，天然按账号隔离。 */
  readonly key: string;
  readonly userId: string;
  readonly workId: string;
  readonly chapterId: string;
  readonly doc: Record<string, unknown>;
  /** 这份草稿基于服务端的哪个 revision；用于判断它是否已经落后。 */
  readonly baseRevision: number;
  readonly updatedAt: number;
}

export interface LocalEditRow {key:string;record:unknown;updatedAt?:number;heldForReview?:boolean}

class DraftDatabase extends Dexie {
  drafts!: Table<LocalDraft, string>;
  edits!: Table<LocalEditRow, string>;
  cache!:Table<{key:string;value:unknown;updatedAt:number},string>;

  constructor() {
    super("novelwrite-offline");
    this.version(1).stores({
      drafts: "key, userId, workId, chapterId, updatedAt"
    });
    this.version(2).stores({ edits: "key" });
    this.version(3).stores({cache:"key,updatedAt"});
  }
}

let database: DraftDatabase | undefined;

function db(): DraftDatabase {
  database ??= new DraftDatabase();
  return database;
}

function keyOf(userId: string, chapterId: string): string {
  return userId + ":" + chapterId;
}

export async function putDraft(draft: {
  userId: string;
  workId: string;
  chapterId: string;
  doc: Record<string, unknown>;
  baseRevision: number;
}): Promise<void> {
  await db().drafts.put({
    key: keyOf(draft.userId, draft.chapterId),
    userId: draft.userId,
    workId: draft.workId,
    chapterId: draft.chapterId,
    doc: draft.doc,
    baseRevision: draft.baseRevision,
    updatedAt: Date.now()
  });
}

export async function getDraft(userId: string, chapterId: string): Promise<LocalDraft | undefined> {
  return db().drafts.get(keyOf(userId, chapterId));
}

export async function deleteDraft(userId: string, chapterId: string): Promise<void> {
  await db().drafts.delete(keyOf(userId, chapterId));
}

/** 换账号时清掉本机草稿，避免把上一个账号的内容带进新会话。 */
export async function clearDraftsForUser(userId: string): Promise<void> {
  await db().drafts.where("userId").equals(userId).delete();
}

export async function listDraftsForUser(userId: string): Promise<LocalDraft[]> {
  return db().drafts.where("userId").equals(userId).toArray();
}

export async function readEdit<T>(key: string): Promise<T | undefined> {
  return (await db().edits.get(key))?.record as T | undefined;
}

export async function writeEdit(key: string, record: unknown | null): Promise<void> {
  if (record === null) await db().edits.delete(key);
  else await db().edits.put({ key, record,updatedAt:Date.now() });
}

/** A background acknowledgement may only replace the record it actually read.
 * The comparison and write share one IndexedDB transaction, including across tabs. */
export async function replaceEditIfUnchanged(key:string,expected:unknown|null,record:unknown|null,canWrite:()=>boolean=()=>true,background=false):Promise<boolean> {
  const expectedJson=JSON.stringify(expected);
  return db().transaction("rw",db().edits,async()=>{
    const row=await db().edits.get(key);
    const current=row?.record??null;
    if(!canWrite()||JSON.stringify(current)!==expectedJson)return false;
    if(background&&(row?.heldForReview||(await resourceRows(editResourceKey(key))).length>1))return false;
    if(record===null)await db().edits.delete(key);
    else await db().edits.put({...row,key,record,updatedAt:Date.now()});
    return true;
  });
}

export function editResourceKey(key:string):string {
  const separator=key.indexOf(":copy:");return separator<0?key:key.slice(0,separator);
}

/** Read every local variant of a resource in one snapshot. Old shared keys stay readable. */
async function resourceRows(resourceKey:string):Promise<LocalEditRow[]> {
  const [primary,copies]=await Promise.all([
    db().edits.get(resourceKey),
    db().edits.where(":id").between(resourceKey+":copy:",resourceKey+":copy:\uffff",true,true).toArray()
  ]);
  return [...(primary?[primary]:[]),...copies].sort((a,b)=>(b.updatedAt??0)-(a.updatedAt??0)||a.key.localeCompare(b.key));
}
export async function listResourceEdits(resourceKey:string):Promise<LocalEditRow[]> {
  return db().transaction("r",db().edits,()=>resourceRows(resourceKey));
}

/** Mark alternatives before showing a choice or accepting the server version.
 * The hold survives navigation/restarts even after all but one copy is resolved. */
export async function recoveryEdits(resourceKey:string):Promise<LocalEditRow[]> {
  const visible=await listResourceEdits(resourceKey);
  if(visible.length<=1)return visible;
  return db().transaction("rw",db().edits,async()=>{
    const rows=await resourceRows(resourceKey);
    if(rows.length>1)for(const row of rows){row.heldForReview=true;await db().edits.put(row);}
    return rows;
  });
}
export async function selectEditorRecord<T>(resourceKey:string,key:string|null):Promise<T|undefined> {
  if(key!==null&&editResourceKey(key)!==resourceKey)throw new Error("不能读取其他作品或账号的本机稿件。");
  return db().transaction("rw",db().edits,async()=>{
    const rows=await resourceRows(resourceKey);
    for(const row of rows)if(row.key!==key)await db().edits.put({...row,heldForReview:true});
    return rows.find(row=>row.key===key)?.record as T|undefined;
  });
}

/** Preserve a competing writer's record; only this writer moves to a new key.
 * Cleanup uses the same comparison, so a late save cannot delete newer input. */
export async function persistEditorRecord(resourceKey:string,key:string,expected:unknown|null,record:unknown|null):Promise<string> {
  if(editResourceKey(key)!==resourceKey)throw new Error("本机稿件不属于当前作品内容。");
  const expectedJson=JSON.stringify(expected);
  return db().transaction("rw",db().edits,async()=>{
    const current=(await db().edits.get(key))?.record??null;
    const target=JSON.stringify(current)===expectedJson?key:resourceKey+":copy:"+crypto.randomUUID();
    if(record===null){if(target===key)await db().edits.delete(key);}
    else {
      const siblings=await resourceRows(resourceKey);
      for(const sibling of siblings)if(sibling.key!==target)await db().edits.put({...sibling,heldForReview:true});
      await db().edits.put({key:target,record,updatedAt:Date.now(),heldForReview:false});
    }
    return target;
  });
}

export async function putOfflineCache(key:string,value:unknown){await db().cache.put({key,value,updatedAt:Date.now()});}
export async function readOfflineCache<T>(key:string):Promise<T|undefined>{return (await db().cache.get(key))?.value as T|undefined;}
export async function removeOfflineCache(key:string){await db().cache.delete(key);}
export type PendingOperation="backup.create"|"backup.restore"|"writing.create.work"|"writing.create.chapter"|"writing.create.volume"|"writing.restore.chapter"|"writing.manage";
const operationKey=(ownerId:string,operation:PendingOperation,fingerprint:string)=>"pending-operation:"+JSON.stringify([ownerId,operation,fingerprint]);
/** One command identity across reloads and simultaneous tabs. No archive bytes are stored here. */
export async function claimPendingOperation(ownerId:string,operation:PendingOperation,fingerprint:string):Promise<string>{
 const key=operationKey(ownerId,operation,fingerprint);
 return db().transaction("rw",db().cache,async()=>{
  const existing=await db().cache.get(key);
  if(existing){
   const value=existing.value as {mutationId?:unknown}|null;
   if(typeof value?.mutationId!=="string"||!(/^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i).test(value.mutationId))throw new Error("无法读取上一次操作记录，请先核对已有结果。");
   return value.mutationId;
  }
  const mutationId=crypto.randomUUID();await db().cache.put({key,value:{mutationId},updatedAt:Date.now()});return mutationId;
 });
}
export async function completePendingOperation(ownerId:string,operation:PendingOperation,fingerprint:string,mutationId:string):Promise<void>{
 const key=operationKey(ownerId,operation,fingerprint);
 await db().transaction("rw",db().cache,async()=>{
  const current=(await db().cache.get(key))?.value as {mutationId?:unknown}|undefined;
  if(current?.mutationId===mutationId)await db().cache.delete(key);
 });
}
export interface PendingCommand<T>{mutationId:string;command:T}
export async function readPendingCommand<T>(ownerId:string,operation:PendingOperation,scope:string):Promise<PendingCommand<T>|undefined>{
 const value=(await db().cache.get(operationKey(ownerId,operation,scope)))?.value;
 if(value===undefined)return;
 if(!value||typeof value!=="object"||typeof (value as PendingCommand<T>).mutationId!=="string"||!("command" in value))throw new Error("上一次操作记录损坏，请先保留当前稿件。");
 return value as PendingCommand<T>;
}
/** Freeze the command as well as its ID: after a lost receipt a new head must
 * not replace expectedRevision in the original request. */
export async function claimPendingCommand<T>(ownerId:string,operation:PendingOperation,scope:string,command:T):Promise<PendingCommand<T>>{
 return db().transaction("rw",db().cache,async()=>{
  const existing=await readPendingCommand<T>(ownerId,operation,scope);if(existing)return existing;
  const value={mutationId:crypto.randomUUID(),command:structuredClone(command)};
  await db().cache.put({key:operationKey(ownerId,operation,scope),value,updatedAt:Date.now()});return value;
 });
}
/** A restore may finish after the author resumed editing. Keep that input as
 * an explicit recovery choice, never as an automatic background overwrite. */
export async function stashRecoveryDraft(resourceKey:string,record:unknown):Promise<string>{
 const copy=structuredClone(record),json=JSON.stringify(copy);
 return db().transaction("rw",db().edits,async()=>{
  const existing=(await resourceRows(resourceKey)).find(row=>row.heldForReview&&JSON.stringify(row.record)===json);
  if(existing)return existing.key;
  const key=resourceKey+":copy:"+crypto.randomUUID();await db().edits.put({key,record:copy,updatedAt:Date.now(),heldForReview:true});return key;
 });
}
export async function listEdits(userId:string){return db().edits.where(":id").between(userId+":",userId+":\uffff",true,true).toArray();}
